// A city's year, rendered as strings: the charts, the calendar, the readings.
//
// These are the primitives. The page they are arranged into — the guide and the
// briefing — is lib/place-city.ts, and the compare view arranges the same ones
// for two or three places (lib/compare-view.ts).
//
// Every function here takes a series (plus the calendar keys indexing it) and
// returns HTML or SVG. Nothing touches the DOM, fetches, or reads app state — the
// client's city panel decides WHEN to render and WHERE to put the result; this
// module decides only what it says. That split is what makes the briefing
// testable: the arithmetic lives in city-stats.ts, the wording and the geometry
// live here, and both can be checked without a browser.
//
// Colours come from lib/colors.ts, the same ramp the map field is painted with,
// so a chart bar and the pixel under it agree.

import { colorAt } from './colors.ts';
import { VAR } from './variables.ts';
import { sectionLabelShort } from './sections.ts';
import { MONTH_FULL, MONTH_INITIAL, MONTH_ABBR, monthlyMeans, meanOf, extentIdx, trendSmooth, cumulativeRainAt, bestMonthFor, driverState, limitingFactor } from './city-stats.ts';
import { yearsPhrase } from './year-span.ts';
import type { CitySeries } from '../contracts/city.ts';
import type { VarKey } from './variables.ts';
import type { Factor } from './recipe.ts';

export function escapeHtml(s: unknown): string {
  return String(s == null ? '' : s).replace(/[&<>"]/g, (c) => (({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }) as Record<string, string>)[c]);
}

// "Early Jul" — short month, for record labels and chart readouts.
export function shortLabel(dates: string[], i: number): string {
  const key = dates[i];
  return key ? sectionLabelShort(key) : '';
}

// Smoothed series cache — the readout reports the same smoothed value the line and
// day-marker sit on (raw daily normals are too spiky to read a single day off).
// The cache rides on the series object itself, so it is dropped with it.
export function smoothedCached(s: CitySeries, vk: string, dates: string[]): (number | null)[] | null {
  s._sm = s._sm || {};
  if (!(vk in s._sm)) s._sm[vk] = s.series[vk] ? trendSmooth(s.series[vk], 9, dates.length) : null;
  return s._sm[vk];
}

// ── chart geometry ───────────────────────────────────────────────────────────
// Shared bar-chart geometry. Bars are grouped three-per-month with a gap between
// months (the "best for" calendar's grouping, applied to the diagrams), so spacing
// is NOT uniform — the chart renderer and the hover/scrub mapping both read positions
// from here so they stay in lockstep.
export const CHART_W = 340;

export function barGeom(n: number, W: number = CHART_W): { x: number; w: number; cx: number }[] {
  const per = 3; // sections per month group
  const groups = Math.ceil(n / per);
  // Gaps are a FRACTION of the width so the grouping stays proportional across charts
  // of different viewBox widths (the wide temperature chart vs the small ones) — which
  // also keeps cx/W identical everywhere, so the hover mapping (which reads barGeom at
  // CHART_W) matches whatever width a chart was actually drawn at.
  const gGap = W * 0.0145; // between month groups
  const bGap = W * 0.0035; // between the bars inside a month
  // Below ~4 groups the grouping is meaningless (e.g. a daily fallback) — space evenly.
  if (n > 48 || n % per !== 0) {
    const bw = W / n;
    return Array.from({ length: n }, (_, i) => ({ x: i * bw + 0.5, w: Math.max(0.5, bw - 1), cx: (i + 0.5) * bw }));
  }
  const groupW = (W - (groups - 1) * gGap) / groups;
  const bw = (groupW - (per - 1) * bGap) / per;
  const geom: { x: number; w: number; cx: number }[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const g = Math.floor(i / per), pos = i % per;
    const x = g * (groupW + gGap) + pos * (bw + bGap);
    geom[i] = { x, w: bw, cx: x + bw / 2 };
  }
  return geom;
}

// ── charts (framework-agnostic SVG) ──────────────────────────────────────────

// Every non-data colour the charts use, in one place.
//
// A TS const rather than CSS custom properties, and that is deliberate:
// sectionBarChart returns a STRING that city-view.test.ts asserts on as text,
// and colorAt already writes rgb(...) literals into the same string. One source
// keeps the SVG testable without a browser. The twins of these values live in
// :root as --fc-chart-*, the same hand-mirroring convention breakpoints.ts uses
// against the stylesheet.
//
// What is NOT here: anything that colours DATA. Bars take their fill from
// colorAt, i.e. from the variable's own ramp in variables.ts, which the redesign
// leaves alone.
export const CHART_THEME = {
  grid: '#18232e',       // horizontal gridlines
  axis: '#8d9aa6',       // value labels and month names
  zero: '#2a3b4a',       // the zero baseline, when the data crosses it
  highlight: '#3fbf85',  // the current dekad's backing rect
  highlightOpacity: 0.14,
  barRadius: 1.5,
  barOpacity: 0.8,       // bars that are not the current dekad
  line: '#eef2f5',       // the feels-like overlay
  lineWidth: 1.6,
  lineOpacity: 0.72,
  axisSize: 10,
} as const;
// One renderer for every year diagram: a bar per calendar section, coloured by the
// variable's own ramp, current section brightened. Bars are grouped three-per-month
// (via barGeom, same as the "best for" calendar) so the eye reads beginning/middle/end
// within each month — and reads them as the section estimate they are, not a precise
// daily line.
//
export interface BarChartOpts {
  /** One value per calendar section; nulls leave a gap. */
  data: (number | null)[];
  /** Whose ramp colours the bars (colorAt). */
  varKey: VarKey;
  /** Section to brighten as "now". */
  curIdx: number | null;
  /** ViewBox width — stays ~CHART_W so axis text is legible in a third-of-row cell. */
  W?: number;
  H: number;
  /** Fixed [lo, hi] range; omitted means auto from the data. */
  dom?: [number, number] | null;
  gridStep?: number;
  /** Bars grow from 0, up and down — temperature/feels-like. */
  zeroBaseline?: boolean;
  /** Drawn as a connected line + dots on the SAME scale (feels-like over temp bars). */
  secondary?: { data: (number | null)[]; color: string; style: string } | null;
  monthNames?: string[];
  /** Extra class on the <svg>, so a pre-rendered page can carry two widths of the
   *  same chart and let CSS choose (see cityCharts' `dual`). */
  extraClass?: string;
}

export function sectionBarChart(opts: BarChartOpts): string {
  const { data, varKey, curIdx, W = CHART_W, H, dom = null, gridStep, zeroBaseline = false, secondary = null, monthNames = MONTH_INITIAL, extraClass } = opts;
  const cls = extraClass ? `chart ${extraClass}` : 'chart';
  const padT = 12, padB = 20;
  const n = data.length;
  const geom = barGeom(n, W);
  const defined = data.filter((v): v is number => v != null);
  if (!defined.length) return `<svg class="${cls}"></svg>`;
  // The secondary line shares the scale, so widen the range to hold it too.
  const rangeVals = secondary && secondary.data ? defined.concat(secondary.data.filter((v): v is number => v != null)) : defined;

  let lo: number, hi: number;
  if (dom) {
    [lo, hi] = dom;
  } else if (zeroBaseline) {
    // Keep 0 as the baseline, but only extend the axis past it where the data
    // actually goes — an all-positive city (Madrid) shouldn't show a phantom -5.
    const dmax = Math.max(...rangeVals), dmin = Math.min(...rangeVals);
    hi = dmax > 0 ? Math.ceil((dmax + 2) / 5) * 5 : 0;
    lo = dmin < 0 ? Math.floor((dmin - 2) / 5) * 5 : 0;
  } else {
    lo = 0;
    hi = Math.max(1, ...rangeVals) * 1.14;
  }
  const span = hi - lo || 1;
  const yTop = padT, yBot = H - padB;
  const Y = (v: number) => yTop + (1 - (v - lo) / span) * (yBot - yTop);
  const baseY = Y(Math.max(lo, Math.min(hi, zeroBaseline ? 0 : lo)));

  // horizontal gridlines + left value labels
  let grid = '';
  const gstep = gridStep || Math.max(1, Math.round(span / 4 / 5) * 5) || 1;
  for (let g = Math.ceil(lo / gstep) * gstep; g < hi - gstep * 0.35; g += gstep) {
    const y = Y(g);
    grid += `<line x1="0" x2="${W}" y1="${y.toFixed(1)}" y2="${y.toFixed(1)}" stroke="${CHART_THEME.grid}" stroke-width="1"/>`;
    grid += `<text x="2" y="${(y - 2).toFixed(1)}" fill="${CHART_THEME.axis}" font-size="${CHART_THEME.axisSize}">${g}</text>`;
  }
  if (zeroBaseline && lo < 0 && hi > 0) {
    grid += `<line x1="0" x2="${W}" y1="${baseY.toFixed(1)}" y2="${baseY.toFixed(1)}" stroke="${CHART_THEME.zero}" stroke-width="1"/>`;
  }

  // current-section highlight, behind its bar
  let hl = '';
  if (curIdx != null && geom[curIdx]) {
    const b = geom[curIdx];
    hl = `<rect x="${(b.x - 1).toFixed(1)}" y="${yTop}" width="${(b.w + 2).toFixed(1)}" height="${(yBot - yTop).toFixed(1)}" rx="2" fill="${CHART_THEME.highlight}" opacity="${CHART_THEME.highlightOpacity}"/>`;
  }

  // bars
  let bars = '';
  data.forEach((v, i) => {
    if (v == null) return;
    const b = geom[i];
    const y = Y(v);
    const top = Math.min(y, baseY);
    const h = Math.max(1, Math.abs(y - baseY));
    const col = colorAt(varKey, v);
    bars += `<rect x="${b.x.toFixed(1)}" y="${top.toFixed(1)}" width="${b.w.toFixed(1)}" height="${h.toFixed(1)}" rx="${CHART_THEME.barRadius}" fill="${col}" opacity="${i === curIdx ? 1 : CHART_THEME.barOpacity}"/>`;
  });

  // secondary series: a prominent connected line with dots on the same scale (feels-like
  // over the temperature bars).
  let sec = '';
  if (secondary && secondary.data) {
    const pts = secondary.data.map((v, i) => (v == null || !geom[i] ? null : `${geom[i].cx.toFixed(1)},${Y(v).toFixed(1)}`)).filter(Boolean).join(' ');
    // A bare line. The 36 dots that used to sit on it were r=2.1 — too small to
    // read a value off, and saying only what the line already said.
    sec += `<polyline points="${pts}" fill="none" stroke="${secondary.color}" stroke-width="${CHART_THEME.lineWidth}" opacity="${CHART_THEME.lineOpacity}" stroke-linejoin="round" vector-effect="non-scaling-stroke"/>`;
  }

  // month ticks — labelled under the middle (Mid) section of each month group
  let mticks = '';
  data.forEach((_, i) => {
    if (i % 3 !== 1 || !geom[i]) return;
    mticks += `<text x="${geom[i].cx.toFixed(1)}" y="${H - 6}" fill="${CHART_THEME.axis}" font-size="${CHART_THEME.axisSize}" text-anchor="middle">${monthNames[Math.floor(i / 3)]}</text>`;
  });

  return `<svg class="${cls}" viewBox="0 0 ${W} ${H}">${grid}${hl}${bars}${sec}${mticks}</svg>`;
}

// ── several places on one axis ───────────────────────────────────────────────
// The compare view draws a measurement for two or three places as LINES on one
// shared scale — bars side by side would be three charts pretending to be one.
// Same geometry as the bars (barGeom's centres, so a section sits where it does
// in every other chart), same gridlines and current-section highlight.
//
// A line's colour is the place's identity, which is chrome rather than data, so
// it is a class (`cls`) the stylesheet colours, not a literal in the string.
export interface OverlayLineOpts {
  lines: { data: (number | null)[]; cls: string; title?: string }[];
  curIdx: number | null;
  W?: number;
  H: number;
  /** [lo, hi]; widened to whole grid steps if any line leaves it. */
  dom: [number, number];
  gridStep: number;
  strokeWidth?: number;
  monthNames?: string[];
}

export function overlayLineChart(opts: OverlayLineOpts): string {
  const { lines, curIdx, W = CHART_W, H, gridStep, strokeWidth = 1.8, monthNames = MONTH_INITIAL } = opts;
  const n = Math.max(0, ...lines.map((l) => l.data.length));
  if (!n) return '<svg class="chart"></svg>';
  const geom = barGeom(n, W);
  const all = lines.flatMap((l) => l.data.filter((v): v is number => v != null));
  let [lo, hi] = opts.dom;
  // The stated range is the design's; the data is the fact. A place colder than
  // the axis starts does not get its line cut off — the axis moves.
  if (all.length) {
    lo = Math.min(lo, Math.floor(Math.min(...all) / gridStep) * gridStep);
    hi = Math.max(hi, Math.ceil(Math.max(...all) / gridStep) * gridStep);
  }
  const padT = 12, padB = 20;
  const yTop = padT, yBot = H - padB;
  const span = hi - lo || 1;
  const Y = (v: number) => yTop + (1 - (v - lo) / span) * (yBot - yTop);

  let grid = '';
  for (let g = Math.ceil(lo / gridStep) * gridStep; g < hi - gridStep * 0.35; g += gridStep) {
    const y = Y(g);
    grid += `<line x1="0" x2="${W}" y1="${y.toFixed(1)}" y2="${y.toFixed(1)}" stroke="${CHART_THEME.grid}" stroke-width="1"/>`;
    grid += `<text x="2" y="${(y - 2).toFixed(1)}" fill="${CHART_THEME.axis}" font-size="${CHART_THEME.axisSize}">${g}</text>`;
  }
  let hl = '';
  if (curIdx != null && geom[curIdx]) {
    const b = geom[curIdx];
    hl = `<rect x="${(b.x - 1).toFixed(1)}" y="${yTop}" width="${(b.w + 2).toFixed(1)}" height="${(yBot - yTop).toFixed(1)}" rx="2" fill="${CHART_THEME.highlight}" opacity="${CHART_THEME.highlightOpacity}"/>`;
  }
  // A gap in the data is a gap in the line: each defined run is its own polyline.
  let paths = '';
  for (const l of lines) {
    let run: string[] = [];
    const flush = () => {
      if (run.length > 1) paths += `<polyline class="cmp-line ${l.cls}" points="${run.join(' ')}" fill="none" stroke-width="${strokeWidth}" stroke-linejoin="round" vector-effect="non-scaling-stroke"/>`;
      run = [];
    };
    l.data.forEach((v, i) => {
      if (v == null || !geom[i]) { flush(); return; }
      run.push(`${geom[i].cx.toFixed(1)},${Y(v).toFixed(1)}`);
    });
    flush();
  }
  let mticks = '';
  for (let i = 1; i < n; i += 3) {
    if (!geom[i]) continue;
    mticks += `<text x="${geom[i].cx.toFixed(1)}" y="${H - 6}" fill="${CHART_THEME.axis}" font-size="${CHART_THEME.axisSize}" text-anchor="middle">${monthNames[Math.floor(i / 3)]}</text>`;
  }
  return `<svg class="chart" viewBox="0 0 ${W} ${H}">${grid}${hl}${paths}${mticks}</svg>`;
}

// ── hover readouts ───────────────────────────────────────────────────────────
export function chartReadout(s: CitySeries, id: string, i: number, dates: string[]): string {
  const date = shortLabel(dates, i);
  const at = (vk: string) => { const a = smoothedCached(s, vk, dates); return a ? a[i] : null; };
  const pct = (vk: string) => { const x = at(vk); return `${date} · ${x != null ? Math.round(x) + '%' : '—'}`; };
  if (id === 'chart-tempfeels') { const t = at('t'), a = at('app'); return `${date} · ${t != null ? Math.round(t) + '°' : '—'} · feels ${a != null ? Math.round(a) + '°' : '—'}`; }
  if (id === 'chart-rainpct') return pct('rain');
  if (id === 'chart-sky') return pct('sky');
  if (id === 'chart-rainmm') {
    const mm = cumulativeRainAt(s, i, dates);
    return `${date} · ${mm != null ? Math.round(mm) + ' mm' : '—'}`;
  }
  const v = at('ws');
  return `${date} · ${v != null ? v.toFixed(1) + ' m/s' : '—'}`;
}

// ── "At a glance": every measurement for the selected section ────────────────
export const SUMMARY_ROWS: { k: string; label: string; fmt: (v: number) => string }[] = [
  { k: 't', label: 'Temperature', fmt: (v) => `${Math.round(v)}°C` },
  { k: 'app', label: 'Feels like', fmt: (v) => `${Math.round(v)}°C` },
  { k: 'd', label: 'Humidity (dew pt)', fmt: (v) => `${Math.round(v)}°C` },
  { k: 'ws', label: 'Wind speed', fmt: (v) => `${v.toFixed(1)} m/s` },
  { k: 'rain', label: 'Chance of rain', fmt: (v) => `${Math.round(v)}%` },
  { k: 'rainmm', label: 'Rainfall', fmt: (v) => `${Math.round(v)} mm` },
  { k: 'sky', label: 'Cloud cover', fmt: (v) => `${Math.round(v)}%` },
  { k: 'v', label: 'Haze & fog', fmt: (v) => `${Math.round(v / 1000)} km vis` },
];

export function summaryHtml(s: CitySeries, i: number, dates: string[]): string {
  const at = (vk: string) => { const a = smoothedCached(s, vk, dates); return a ? a[i] : null; };
  return SUMMARY_ROWS.map((r) => {
    const v = r.k === 'rainmm' ? cumulativeRainAt(s, i, dates) : at(r.k);
    return `<div class="sum-row"><span class="sum-label">${r.label}</span><span class="sum-val">${v != null ? r.fmt(v) : '—'}</span></div>`;
  }).join('');
}

// ── score details ────────────────────────────────────────────────────────────
// Short name + compact measurement for a driver's variable, so each row reads as
// "<what was measured> — <verdict>".
export const FACTOR_SHORT: Record<string, string> = { app: 'Feels', t: 'Temp', d: 'Humidity', rain: 'Rain', ws: 'Wind', sky: 'Cloud', v: 'Visibility' };

export function fmtMeasure(vk: string, val: number | null | undefined): string {
  if (val == null) return '—';
  if (vk === 'app' || vk === 't' || vk === 'd') return `${Math.round(val)}°`;
  if (vk === 'ws') return `${val.toFixed(1)} m/s`;
  if (vk === 'v') return `${Math.round(val / 1000)} km`;
  return `${Math.round(val)}%`; // rain, sky
}

// How each factor is NAMED in "held back by …". FACTOR_SHORT is a column label
// ("Feels", "Cloud") and reads wrong in a sentence — "held back by feels" is not
// English. One extra map is cheaper than bending the column headings.
export const WHY_NOUN: Record<string, string> = {
  app: 'how it feels', t: 'temperature', d: 'humidity',
  rain: 'rain', ws: 'wind', sky: 'cloud', v: 'haze',
};

// The score details, in whichever state the reader last left them.
//
// One renderer for the transient surfaces that state a score in passing — the
// dot tooltip and the peaks rows — because they are two views of the same
// question and answering it two ways would make the shared preference feel like
// two unrelated toggles. The briefing's masthead does not use this: there the
// drivers are always expanded, and they are half the page rather than an aside.
//
// Collapsed is not "nothing": it still names the limiting factor, which is the
// part a reader most often wants. Expanded is the full breakdown — the
// MEASUREMENT first, its ASSESSMENT after (✓ ideal / ~ marginal / ✗ outside),
// colour-coded, so the number and the verdict that follows from it read
// together rather than as two disjoint lists.
export function whyHtml(
  factors: Factor[],
  at: (v: string) => number | null | undefined,
  expanded: boolean,
): string {
  if (!factors.length) return '';
  const toggle = `<button type="button" class="why-toggle" data-why>${expanded ? 'Hide why' : 'Why?'}</button>`;
  if (!expanded) {
    const worst = limitingFactor(factors, at);
    const line = worst
      ? `<span class="why-lim drv-${worst.cls}">held back by ${WHY_NOUN[worst.factor.v] || VAR[worst.factor.v as VarKey].label.toLowerCase()}</span>`
      : '<span class="why-lim drv-good">everything lines up</span>';
    return `<div class="why">${line}${toggle}</div>`;
  }
  const rows = factors
    .map((f) => {
      const x = at(f.v);
      const measure = `${FACTOR_SHORT[f.v] || VAR[f.v as VarKey].label} ${fmtMeasure(f.v, x)}`;
      if (x == null) return `<div class="cd-row"><span class="cd-measure">${measure}</span><span class="cd-verdict drv-na">–</span></div>`;
      const st = driverState(f, Number(x));
      return `<div class="cd-row"><span class="cd-measure">${measure}</span><span class="cd-verdict drv-${st.cls}">${st.mark} ${st.text}</span></div>`;
    })
    .join('');
  return `<div class="why"><div class="cal-drivers">${rows}</div>${toggle}</div>`;
}

// ── written summary + advice, generated from the real series ─────────────────
// The months rain is most and least likely in. From the all-day climate when
// the series carries it — one time of day misses the tropics' afternoon storms
// and the night rain — and from this bucket's readings when it does not.
function wetDryMonths(s: CitySeries, dates: string[]): { wettest: string; driest: string } {
  const rain = s.climate ? s.climate.months.map((m) => m.rain) : monthlyMeans(s.series.rain, dates);
  return {
    wettest: MONTH_FULL[extentIdx(rain, (a, b) => a > b)] || '—',
    driest: MONTH_FULL[extentIdx(rain, (a, b) => a < b)] || '—',
  };
}

export function describeCity(s: CitySeries, dates: string[], years: string[]): string {
  const { wettest, driest } = wetDryMonths(s, dates);
  const span = yearsPhrase(years);
  const blend = `Every figure here is ${span ? `${span} of observations ` : ''}blended into one representative year, so it reads as what this date is usually like, not a forecast for one particular day.`;
  const c = s.climate;
  const his = c ? c.months.map((m) => m.hi) : [];
  const los = c ? c.months.map((m) => m.lo) : [];
  const coolI = extentIdx(his, (a, b) => a < b);
  const warmI = extentIdx(his, (a, b) => a > b);
  // The whole day's range, when the series carries it: a reader asking what a
  // place's climate is means its nights as much as its afternoons, and a mean
  // read at midday alone overstates a highland town by five degrees.
  if (c && c.mean != null && coolI >= 0 && warmI >= 0) {
    const loMin = Math.min(...los.filter((v): v is number => v != null));
    const loMax = Math.max(...los.filter((v): v is number => v != null));
    const lows = loMin.toFixed(0) === loMax.toFixed(0)
      ? `sit around ${loMin.toFixed(0)}°C all year`
      : `run from ${loMin.toFixed(0)}°C to ${loMax.toFixed(0)}°C through the year`;
    return escapeHtml(
      `Year round, ${s.name} has days of about ${his[coolI]!.toFixed(0)}°C at their warmest in ${MONTH_FULL[coolI]}, its coolest month, and ${his[warmI]!.toFixed(0)}°C in ${MONTH_FULL[warmI]}, its warmest; ` +
        `the coolest hours of the day ${lows}, and the average temperature over the year is ${c.mean.toFixed(0)}°C around the clock. ` +
        `Rain is most likely around ${wettest} and least likely around ${driest}. ` +
        blend,
    );
  }
  const monT = monthlyMeans(s.series.t, dates);
  const tMax = Math.max(...monT.filter((v): v is number => v != null));
  const tMin = Math.min(...monT.filter((v): v is number => v != null));
  const annual = meanOf(s.series.t);
  return escapeHtml(
    `In a typical year, ${s.name} swings from about ${tMin.toFixed(0)}°C in its coolest month to ${tMax.toFixed(0)}°C at the height of summer, averaging ${annual!.toFixed(1)}°C. ` +
      `Rain runs heaviest around ${wettest} and thins out near ${driest}. ` +
      blend,
  );
}

// ── the "best for" calendar ──────────────────────────────────────────────────
// Five ratings down, thirty-six ten-day sections across, grouped three-per-month
// so a month reads as a unit — the same grouping barGeom gives the diagrams.
//
// A string rather than DOM built cell by cell, for the same reason everything
// else here is: the pre-render builds this calendar in Node, and the client
// builds it in the browser, and neither can be allowed to drift from the other.
// The client attaches its hover/click/touch behaviour to the result by
// delegation (see buildCalendar) — the markup carries data-a/data-i, which is
// all those handlers ever read off a cell.
//
// `curIdx` marks the section the app is scrubbed to and `pin` the pinned cell;
// both are null on a pre-rendered page, which names no particular date.
// The best-scoring of a month's three ten-day sections, as an index into the
// year. Falls back to the month's middle section when the month has no readings
// at all, so the peak column always points somewhere sensible.
function peakSectionOf(series: (number | null)[], month: number): number {
  const first = month * 3;
  let bestI = first + 1, bestV = -Infinity;
  for (let i = first; i < first + 3; i++) {
    const v = series[i];
    if (v != null && v > bestV) { bestV = v; bestI = i; }
  }
  return bestI;
}

export interface CalendarOpts {
  curIdx?: number | null;
  pin?: { a: number; i: number } | null;
  /** The rating the page is showing: its row reads as selected. */
  active?: VarKey | null;
  /** Each rating's paragraph, as HTML, to open under its row (the guide). */
  prose?: Partial<Record<string, string>> | null;
  /** Offer "My kind of day" as a last row when it is not already one of `acts`. */
  myday?: boolean;
}

export function calendarHtml(
  s: CitySeries,
  dates: string[],
  acts: VarKey[],
  { curIdx = null, pin = null, active = null, prose = null, myday = false }: CalendarOpts = {},
): string {
  const n = dates.length; // 36 sections
  const rows = acts.map((a, ai) => {
    const series = s.series[a] || [];
    let cells = '';
    for (let i = 0; i < n; i++) {
      // Three sections per month share a group box.
      if (i % 3 === 0) cells += (i ? '</div>' : '') + '<div class="cal-monthgroup">';
      const sc = series[i];
      // The score rides as --s and the stylesheet turns it into opacity, because
      // the floor is a property of the page's theme (0.3 on dark, 0.45 on light,
      // where a faint cell vanishes into the paper): a wide range either way —
      // the rows are short, so a poor dekad and a good one need the contrast.
      const style = sc != null
        ? `background:${colorAt(a, sc)};--s:${(sc / 100).toFixed(2)}`
        : 'background:var(--fc-hover)';
      const cls = 'cal-cell'
        + (curIdx != null && i === curIdx ? ' cur' : '')
        + (pin && pin.a === ai && pin.i === i ? ' pinned' : '');
      cells += `<div class="${cls}" style="${style}" data-a="${ai}" data-i="${i}"></div>`;
    }
    if (n) cells += '</div>';
    // Where this rating peaks HERE, in its own column at the end of the row —
    // without it a reader compares thirty-six cells by eye to answer the question
    // the row exists to answer. "never good" rather than a number when the best
    // month cannot even reach Fair: "Jan · 14" invites a pointless comparison.
    const best = bestMonthFor(s, a, dates);
    const good = !!best && best.score >= 35;
    const peak = good ? `${best!.month.slice(0, 3)} · ${best!.score}` : 'never good';
    // The peak names a rating AND a month, so clicking it means both — the same
    // pair a cell in this row means, and it goes through the same handler. The
    // section it lands on is the best of that month's three rather than the
    // month's middle one: the column is an answer to "when is it best here", and
    // it should go to the best.
    const peakCell = good
      ? `<button type="button" class="cal-peak cal-goto" data-goto-a="${ai}" data-goto-i="${peakSectionOf(series, MONTH_ABBR.indexOf(best!.month.slice(0, 3)))}">${escapeHtml(peak)}</button>`
      : `<div class="cal-peak cal-peak-none">${escapeHtml(peak)}</div>`;
    // The rating's own paragraph, in the document for every row and shown under
    // the selected one (the stylesheet's .cal-row.active rule) — so a crawler
    // reads all of them and a reader sees the one they asked about.
    const para = prose && prose[a] ? `<p class="cal-prose">${prose[a]}</p>` : '';
    const custom = !!VAR[a].custom;
    // The name is the control that selects the row's rating. On the reader's own
    // rating it also opens the editor that defines it.
    return `<div class="cal-row${a === active ? ' active' : ''}${custom ? ' cal-row-myday' : ''}" data-act="${a}">`
      + `<div class="cal-line">`
      + `<button type="button" class="cal-label" data-pick-a="${ai}"${custom ? ' data-myday-open' : ''}>${escapeHtml(VAR[a].label)}</button>`
      + `<div class="cal-cells">${cells}</div>`
      + peakCell
      + `</div>${para}`
      + (custom ? '<div class="pl-myday-anchor"></div>' : '')
      + `</div>`;
  }).join('');
  // "My kind of day", before it is anybody's: the row is an invitation, and the
  // cells arrive when the reader has said what a good day is.
  const unset = myday && !acts.some((a) => VAR[a].custom)
    ? `<div class="cal-row cal-row-myday cal-row-unset" data-act="custom"><div class="cal-line">`
      + `<button type="button" class="cal-label" data-myday-open>My kind of day</button>`
      + `<button type="button" class="cal-cells cal-myday-cta" data-myday-open>Describe a day you’d love — this row fills in as you go ›</button>`
      + `<button type="button" class="cal-peak cal-myday-setup" data-myday-open>Set up ›</button>`
      + `</div><div class="pl-myday-anchor"></div></div>`
    : '';
  // The ruler's own month scale, repeated here as the calendar's axis — same
  // labels, so a reader crossing between them is reading one scale, not two.
  const months = `<div class="cal-months">${MONTH_ABBR.map((m) => `<div class="cal-month">${m}</div>`).join('')}</div>`;
  return rows + unset + months;
}
